import { useState, useEffect, useRef } from 'react'
import { Outlet, useLocation, useNavigate, Link } from 'react-router-dom'
import { hasUnsavedChanges, registerDiscardPrompt } from '@/lib/navGuard'
import Sidebar, { ChangePasswordModal } from './Sidebar'
import TopMenu from './TopMenu'
import AmbienteBanner from './AmbienteBanner'
import AiChat from './AiChat'
import SofiaBubble from './SofiaBubble'
import { Menu, Sparkles, PanelLeftClose, PanelLeft, ArrowLeft, HelpCircle, Pin, PinOff, Sun, Moon, Minus, Plus, LogOut, KeyRound, ChevronDown, RefreshCw, Eye, Pencil } from 'lucide-react'
import GlobalMenuSearch from './GlobalMenuSearch'
import { useReadonly, isAdmin, impAtual, isImpersonando } from '@/lib/readonly'
import { sairImpersonacao } from '@/lib/impersonate'
import { getMenuEstilo, getZoom, aplicarZoom, setZoom as persistZoom, EVT, type MenuEstilo } from '@/lib/aparencia'
import { useAuth } from '@/lib/auth'
import { useFilialLogo } from '@/lib/useFilialLogo'
import NotificacoesBell from './NotificacoesBell'
import FilialSelector from './FilialSelector'
import CanalSelector from './CanalSelector'

const TITLES: Record<string, string> = {
  '/': 'Dashboard', '/vendas': 'Pedidos de Venda', '/vendas/novo': 'Novo Pedido',
  '/cadastros/clientes': 'Clientes', '/cadastros/fornecedores': 'Fornecedores',
  '/cadastros/produtos': 'Produtos', '/cadastros/categorias': 'Categorias',
  '/estoque': 'Estoque', '/financeiro/receber': 'Contas a Receber',
  '/financeiro/pagar': 'Contas a Pagar', '/fiscal': 'Documentos Fiscais',
  '/industrial/ordens': 'Ordens de Produção', '/industrial/fichas': 'Fichas Técnicas',
  '/marketplace': 'Marketplace', '/relatorios': 'Relatórios', '/relatorios/visao-dono': 'Visão do Dono',
  '/configuracoes': 'Configurações', '/configuracoes/empresa': 'Empresa', '/configuracoes/usuarios': 'Usuários', '/configuracoes/depositos': 'Depósitos', '/configuracoes/ia': 'Sofia',
  '/superadmin': 'Super Admin', '/superadmin/empresas': 'Gestão de Empresas',
  '/superadmin/billing': 'Billing & Assinaturas',
}

export default function Layout() {
  const loc = useLocation()
  const nav = useNavigate()
  const title = TITLES[loc.pathname] || 'VELORE ERP'
  const showBack = loc.pathname !== '/'
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [showLogout, setShowLogout] = useState(false)
  const [showDiscard, setShowDiscard] = useState(false)   // confirma descartar dados não salvos ao sair
  const pendingNavRef = useRef<(() => void) | null>(null) // navegação adiada até confirmar o descarte
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(() => {
    try { return localStorage.getItem('pain_sidebar_collapsed') === 'true' } catch { return false }
  })
  // Auto-hide: sidebar fica mini-bar e expande no hover. Padrão = ligado.
  // Quem prefere o sidebar fixo desliga pelo botão de Pin no topbar.
  const [autoHide, setAutoHide] = useState<boolean>(() => {
    try {
      const v = localStorage.getItem('pain_sidebar_autohide')
      return v === null ? true : v === 'true'
    } catch { return true }
  })
  const [hoveringSidebar, setHoveringSidebar] = useState(false)
  const toggleCollapse = () => {
    setSidebarCollapsed(s => {
      const next = !s
      try { localStorage.setItem('pain_sidebar_collapsed', next ? 'true' : 'false') } catch {}
      return next
    })
  }
  const toggleAutoHide = () => {
    setAutoHide(s => {
      const next = !s
      try { localStorage.setItem('pain_sidebar_autohide', next ? 'true' : 'false') } catch {}
      return next
    })
  }
  // Quando auto-hide está LIGADO: sidebar fica mini (w-14), expande sobreposto no hover.
  // Quando DESLIGADO: comportamento clássico (collapsed = botão manual)
  const sidebarExpandido = autoHide ? hoveringSidebar : !sidebarCollapsed
  const [chatOpen, setChatOpen] = useState(false)
  const { user, empresa, refreshEmpresa, logout } = useAuth()

  // ESC = VOLTAR no sistema (fora do PDV), em etapas:
  //  1) modal de logout aberto       → Esc cancela / Enter confirma (logout)
  //  2) algum modal/overlay aberto    → não faz nada (a própria tela fecha no Esc dela)
  //  3) foco num campo de texto       → sai do campo (blur) primeiro, sem navegar
  //  4) já no início e nada aberto    → pergunta se quer sair do sistema (logout)
  //  5) numa sub-tela                 → volta UMA tela pelo histórico (ex: Criar Pedido → Pedidos → Início).
  //                                      Sem histórico interno (entrou por link) → vai direto pro início.
  useEffect(() => {
    // Modais do sistema são backdrops "fixed inset-0" com fundo translúcido (bg-black/.. ou bg-ink-9..).
    const temOverlayAberto = () =>
      Array.from(document.querySelectorAll<HTMLElement>('.fixed.inset-0')).some((el) => {
        const c = typeof el.className === 'string' ? el.className : ''
        return /bg-(black|ink-9)/.test(c) && el.getClientRects().length > 0
      })
    const editandoCampo = () => {
      const el = document.activeElement as HTMLElement | null
      if (!el) return false
      return el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable
    }
    const confirmarDescarte = () => {
      const acao = pendingNavRef.current
      pendingNavRef.current = null
      setShowDiscard(false)
      acao?.()
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Enter' && showDiscard) { e.preventDefault(); confirmarDescarte(); return }
      if (e.key === 'Enter' && showLogout) { e.preventDefault(); logout(); nav('/login'); return }
      if (e.key !== 'Escape') return
      if (showDiscard) { e.preventDefault(); pendingNavRef.current = null; setShowDiscard(false); return } // Esc = continuar editando
      if (showLogout) { e.preventDefault(); setShowLogout(false); return }   // Esc cancela logout
      if (temOverlayAberto()) return                                          // a tela fecha o modal dela
      if (editandoCampo()) { (document.activeElement as HTMLElement)?.blur(); return }
      e.preventDefault()
      if (loc.pathname === '/') { setShowLogout(true); return }               // no início → pergunta logout
      const irPraTras = () => {
        const idx = (window.history.state && window.history.state.idx) || 0
        if (idx > 0) nav(-1)                                                   // volta UMA tela (em etapas)
        else nav('/')                                                          // sem histórico interno → início
      }
      // Dados não salvos na tela? pergunta antes de sair (Enter descarta · Esc continua editando)
      if (hasUnsavedChanges()) { pendingNavRef.current = irPraTras; setShowDiscard(true); return }
      irPraTras()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [loc.pathname, showLogout, showDiscard, nav, logout])

  // Guarda contra sair com dados não salvos por QUALQUER caminho de navegação:
  //  - botões "Voltar" das telas chamam guardedNavigate() → cai no prompt abaixo
  //  - cliques em links internos (menu lateral, topo, qualquer <Link>) são interceptados aqui
  useEffect(() => {
    const abrirPrompt = (acao: () => void) => { pendingNavRef.current = acao; setShowDiscard(true) }
    registerDiscardPrompt(abrirPrompt)
    function onClickCapture(e: MouseEvent) {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
      if (!hasUnsavedChanges()) return
      const a = (e.target as HTMLElement)?.closest?.('a[href]') as HTMLAnchorElement | null
      if (!a || a.target === '_blank' || a.hasAttribute('download')) return
      const href = a.getAttribute('href') || ''
      if (!href.startsWith('/') || href.startsWith('//')) return   // só rotas internas do SPA
      const dest = href.split('#')[0]
      if (!dest || dest === loc.pathname) return                   // mesma tela → ignora
      e.preventDefault(); e.stopPropagation()
      abrirPrompt(() => nav(dest))
    }
    document.addEventListener('click', onClickCapture, true)       // captura: roda antes do <Link>
    return () => { registerDiscardPrompt(null); document.removeEventListener('click', onClickCapture, true) }
  }, [loc.pathname, nav])

  const filialLogo = useFilialLogo()
  const { readonly, setReadonly } = useReadonly()
  const iaEnabled = empresa?.modulos_ativos?.ia !== false
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const [changePwOpen, setChangePwOpen] = useState(false)

  // Botão ATUALIZAR: força o navegador a buscar a versão mais nova do sistema.
  // Desregistra service worker, apaga caches e recarrega — resolve o "tela velha
  // depois de uma atualização" sem o usuário precisar de Ctrl+Shift+R.
  const [atualizando, setAtualizando] = useState(false)
  const forcarAtualizacao = async () => {
    setAtualizando(true)
    try {
      if ('serviceWorker' in navigator) {
        const rs = await navigator.serviceWorker.getRegistrations()
        await Promise.all(rs.map(r => r.unregister()))
      }
      if (window.caches) {
        const ks = await caches.keys()
        await Promise.all(ks.map(k => caches.delete(k)))
      }
    } catch { /* ignora — recarrega mesmo assim */ }
    window.location.reload()
  }

  // Tema global escuro/claro — default ESCURO. Sincroniza .app-dark no <html> e a
  // chave 'velore_tema' (mesma do PDV), gravando a preferência do usuário no navegador.
  const [tema, setTema] = useState<'escuro' | 'claro'>(() => {
    try { return (localStorage.getItem('velore_tema') as 'escuro' | 'claro') || 'escuro' } catch { return 'escuro' }
  })
  useEffect(() => {
    try { localStorage.setItem('velore_tema', tema) } catch {}
    const html = document.documentElement
    if (tema === 'escuro') html.classList.add('app-dark')
    else html.classList.remove('app-dark')
  }, [tema])

  // Aparência: estilo de menu (padrao | flutuante | topo) + zoom da interface.
  // Sincroniza com Configurações via evento global.
  const [menuEstilo, setMenuEstilo] = useState<MenuEstilo>(() => getMenuEstilo())
  const [zoom, setZoom] = useState<number>(() => getZoom())
  useEffect(() => {
    const sync = () => { setMenuEstilo(getMenuEstilo()); setZoom(getZoom()) }
    window.addEventListener(EVT, sync)
    return () => window.removeEventListener(EVT, sync)
  }, [])
  useEffect(() => { aplicarZoom(zoom) }, [zoom])
  const mudarZoom = (delta: number) => { const v = Math.min(130, Math.max(70, zoom + delta)); setZoom(v); persistZoom(v) }
  const isTopo = menuEstilo === 'topo'
  const isFlutuante = menuEstilo === 'flutuante'

  // Refresh empresa.modulos_ativos do backend a cada montagem do Layout
  // pra garantir que toggle de módulos reflete sem precisar re-login
  useEffect(() => { refreshEmpresa() }, [])

  // Escuta evento global pra abrir AiChat de qualquer página (ex: /ajuda)
  useEffect(() => {
    const handler = () => setChatOpen(true)
    window.addEventListener('open-ai-chat', handler)
    return () => window.removeEventListener('open-ai-chat', handler)
  }, [])

  // Atalho F6 = abrir o PDV; F7 = abrir o PDV-Online (badges "F6"/"F7" no menu anunciam).
  // O PDV fica fora do Layout, então este handler só roda nas telas administrativas —
  // sem conflito com o F6/F7 internos do PDV (irCliente/irVendedor).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.shiftKey || e.altKey || e.metaKey) return
      if (e.key === 'F6') { e.preventDefault(); nav('/pdv') }
      else if (e.key === 'F7') { e.preventDefault(); nav('/pdv-online') }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [nav])

  return (
    <div className="flex bg-ink-50 min-h-[calc(100vh/var(--zoom,1))]">
      {!isTopo && (
      <div
        className={`hidden lg:block sticky top-0 self-start h-[calc(100vh/var(--zoom,1))] relative z-40 ${
          autoHide ? 'w-14' : (sidebarCollapsed ? 'w-14' : 'w-64')
        } transition-[width] duration-200`}
        onMouseEnter={() => autoHide && setHoveringSidebar(true)}
        onMouseLeave={() => autoHide && setHoveringSidebar(false)}
      >
        <div className={`absolute top-0 left-0 h-full transition-[width] duration-200 ${
          sidebarExpandido ? 'w-64' : 'w-14'
        } ${isFlutuante ? 'p-2' : ''} ${autoHide && hoveringSidebar ? 'z-40' : 'z-30'}`}>
          <div className={isFlutuante
            ? 'h-full rounded-2xl overflow-hidden shadow-2xl ring-1 ring-black/10'
            : `h-full ${autoHide && hoveringSidebar ? 'shadow-2xl' : ''}`}>
            <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)}
              collapsed={!sidebarExpandido} onToggleCollapse={toggleCollapse} />
          </div>
        </div>
      </div>
      )}
      {/* Mobile drawer */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/50" onClick={() => setSidebarOpen(false)} />
          <div className="relative">
            <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} collapsed={false} />
          </div>
        </div>
      )}

      <div className="flex-1 flex flex-col min-w-0">
        <AmbienteBanner />
        {/* Topbar */}
        <header className="h-14 lg:h-16 bg-white border-b border-ink-200 flex items-center px-4 lg:px-6 sticky top-0 z-10 gap-3">
          <button onClick={() => setSidebarOpen(true)} className="lg:hidden p-2 -ml-2 rounded-md hover:bg-ink-100 text-ink-700">
            <Menu size={20} />
          </button>
          {!autoHide && !isTopo && (
            <button onClick={toggleCollapse}
              title={sidebarCollapsed ? 'Expandir menu' : 'Recolher menu'}
              className="hidden lg:flex p-2 -ml-2 rounded-md hover:bg-ink-100 text-ink-600">
              {sidebarCollapsed ? <PanelLeft size={18} /> : <PanelLeftClose size={18} />}
            </button>
          )}
          {!isTopo && (
            <button onClick={toggleAutoHide}
              title={autoHide
                ? 'Menu auto-esconde (passe o mouse pra abrir). Clique pra fixar.'
                : 'Menu fixado. Clique pra deixar auto-esconder.'}
              className={`hidden lg:flex p-2 rounded-md hover:bg-ink-100 ${
                autoHide ? 'text-ink-500' : 'text-brand-600'
              }`}>
              {autoHide ? <PinOff size={18} /> : <Pin size={18} />}
            </button>
          )}

          {showBack && (
            <button
              onClick={() => nav(-1)}
              title="Voltar"
              className="p-2 rounded-md hover:bg-ink-100 text-ink-600 flex items-center gap-1"
            >
              <ArrowLeft size={18} />
              <span className="hidden sm:inline text-sm">Voltar</span>
            </button>
          )}

          <h1 className="text-base lg:text-lg font-semibold text-ink-900 truncate">{title}</h1>

          <GlobalMenuSearch />

          <div className="ml-auto flex items-center gap-2">
            <button onClick={forcarAtualizacao} disabled={atualizando}
              title="Buscar a versão mais nova do sistema (limpa o cache e recarrega)"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-brand-600 hover:bg-brand-700 text-white text-sm font-bold shadow-sm disabled:opacity-60">
              <RefreshCw size={16} className={atualizando ? 'animate-spin' : ''} />
              <span className="hidden sm:inline">{atualizando ? 'Atualizando…' : 'Atualizar'}</span>
            </button>
            <div className="hidden md:flex items-center rounded-md border border-ink-200 overflow-hidden" title="Zoom da interface (bom pra monitor pequeno)">
              <button onClick={() => mudarZoom(-10)} disabled={zoom <= 70}
                className="px-1.5 py-1.5 hover:bg-ink-100 text-ink-600 disabled:opacity-40"><Minus size={14} /></button>
              <button onClick={() => { setZoom(100); persistZoom(100) }}
                className="px-1 text-[11px] font-mono text-ink-600 hover:bg-ink-100 w-11 text-center" title="Voltar a 100%">{zoom}%</button>
              <button onClick={() => mudarZoom(10)} disabled={zoom >= 130}
                className="px-1.5 py-1.5 hover:bg-ink-100 text-ink-600 disabled:opacity-40"><Plus size={14} /></button>
            </div>
            <button onClick={() => setTema(t => t === 'escuro' ? 'claro' : 'escuro')}
              title={tema === 'escuro' ? 'Mudar para tema claro' : 'Mudar para tema escuro'}
              className="p-2 rounded-md transition-colors hover:bg-ink-100 text-ink-600">
              {tema === 'escuro' ? <Sun size={18} /> : <Moon size={18} />}
            </button>
            {isAdmin() && (
              <button onClick={() => setReadonly(!readonly)}
                title={readonly ? 'Você está só visualizando — clique para liberar a edição' : 'Entrar no modo Visualização (bloqueia edições no sistema todo)'}
                className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-sm font-medium border transition-colors ${readonly ? 'bg-amber-100 border-amber-300 text-amber-800' : 'border-ink-200 text-ink-600 hover:bg-ink-100'}`}>
                {readonly ? <Eye size={16} /> : <Pencil size={16} />}
                <span className="hidden lg:inline">{readonly ? 'Visualização' : 'Edição'}</span>
              </button>
            )}
            {filialLogo && (
              <img src={filialLogo} alt="Logo da filial" title="Logo da filial"
                className="h-8 max-w-[130px] object-contain rounded hidden sm:block" />
            )}
            <FilialSelector />
            <CanalSelector />
            <Link to="/ajuda" title="Central de Ajuda"
              className="p-2 rounded-md transition-colors hover:bg-violet-100 text-ink-600 hover:text-violet-700">
              <HelpCircle size={18} />
            </Link>
            {iaEnabled && (
              <button onClick={() => { window.dispatchEvent(new Event('sofia-show-bubble')); setChatOpen(o => !o) }}
                title="Sofia"
                className={`p-2 rounded-md transition-colors ${chatOpen ? 'bg-brand-100 text-brand-700' : 'hover:bg-ink-100 text-ink-600'}`}>
                <Sparkles size={18} />
              </button>
            )}
            <NotificacoesBell />
            {isTopo && (
              <div className="relative">
                <button onClick={() => setUserMenuOpen(o => !o)}
                  className="flex items-center gap-2 p-1 pr-2 rounded-md hover:bg-ink-100">
                  <span className="w-8 h-8 rounded-full bg-brand-600 text-white flex items-center justify-center text-sm font-semibold">
                    {(user?.nome || '?').charAt(0).toUpperCase()}
                  </span>
                  <span className="hidden xl:block text-sm text-ink-700 max-w-[120px] truncate">{user?.nome}</span>
                  <ChevronDown size={14} className="text-ink-400" />
                </button>
                {userMenuOpen && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setUserMenuOpen(false)} />
                    <div className="absolute right-0 top-full mt-1 z-50 w-56 bg-white border border-ink-200 rounded-xl shadow-xl py-1.5">
                      <div className="px-3.5 py-2 border-b border-ink-100">
                        <div className="text-sm font-semibold text-ink-900 truncate">{user?.nome}</div>
                        <div className="text-xs text-ink-500 truncate">{user?.email}</div>
                        <div className="text-[11px] text-ink-400 capitalize">{(user?.perfil || '').replace('_', ' ')}</div>
                      </div>
                      {isAdmin() && (
                        <Link to="/configuracoes" onClick={() => setUserMenuOpen(false)}
                          className="flex items-center gap-2 px-3.5 py-2 text-sm text-ink-700 hover:bg-ink-50">
                          <Sparkles size={15} className="text-ink-400" /> Configurações
                        </Link>
                      )}
                      <button onClick={() => { setUserMenuOpen(false); setChangePwOpen(true) }}
                        className="w-full flex items-center gap-2 px-3.5 py-2 text-sm text-ink-700 hover:bg-ink-50">
                        <KeyRound size={15} className="text-ink-400" /> Trocar senha
                      </button>
                      <div className="h-px bg-ink-100 my-1" />
                      <button onClick={() => { setUserMenuOpen(false); logout(); nav('/login') }}
                        className="w-full flex items-center gap-2 px-3.5 py-2 text-sm text-red-600 hover:bg-red-50">
                        <LogOut size={15} /> Sair do sistema
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </header>

        {isImpersonando() && (
          <div className={`text-white text-xs font-semibold px-4 py-1 flex items-center justify-center gap-2 sticky top-14 lg:top-16 z-[9] ${impAtual()?.modo === 'visualizacao' ? 'bg-amber-600' : 'bg-indigo-600'}`}>
            {impAtual()?.modo === 'visualizacao' ? <Eye size={13} /> : <Pencil size={13} />}
            Você está como <b>{impAtual()?.nome}</b> — modo {impAtual()?.modo === 'visualizacao' ? 'VISUALIZAÇÃO (só leitura)' : 'EDIÇÃO'}.
            <button onClick={sairImpersonacao} className="underline hover:no-underline inline-flex items-center gap-1"><ArrowLeft size={12} /> Voltar à minha conta</button>
          </div>
        )}

        {!isImpersonando() && readonly && isAdmin() && (
          <div className="bg-amber-500 text-white text-xs font-semibold px-4 py-1 flex items-center justify-center gap-2 sticky top-14 lg:top-16 z-[9]">
            <Eye size={13} /> MODO VISUALIZAÇÃO — edições bloqueadas no sistema inteiro.
            <button onClick={() => setReadonly(false)} className="underline hover:no-underline">Sair do modo</button>
          </div>
        )}

        {/* Barra de navegação no topo (estilo de menu = topo) */}
        {isTopo && (
          <div className="hidden lg:block bg-white border-b border-ink-200 px-4 lg:px-6 py-1.5 sticky top-14 lg:top-16 z-[9]">
            <TopMenu />
          </div>
        )}

        {/* Main + Chat */}
        <div className="flex-1 flex min-h-0">
          <main className="flex-1 p-4 lg:p-6 overflow-auto flex flex-col">
            <div className="flex-1">
              <Outlet />
            </div>
            <footer className="mt-6 pt-3 border-t border-ink-200 text-center text-xs text-ink-400">
              Desenvolvido por <span className="font-medium text-ink-500">DN Tecnologia</span>
            </footer>
          </main>

          {/* Chat desktop: janelinha flutuante no canto */}
          {chatOpen && iaEnabled && (
            <div className="hidden sm:flex flex-col fixed bottom-6 right-6 z-40 w-[380px] h-[600px] max-h-[calc((100vh/var(--zoom,1))-2rem)] rounded-2xl bg-white shadow-2xl border border-ink-200 overflow-hidden">
              <AiChat onClose={() => setChatOpen(false)} />
            </div>
          )}
        </div>

        {/* Chat mobile: fullscreen overlay */}
        {chatOpen && iaEnabled && (
          <div className="sm:hidden fixed inset-0 z-50 bg-white flex flex-col">
            <AiChat onClose={() => setChatOpen(false)} />
          </div>
        )}
      </div>

      {/* Bolinha flutuante da Sofia (arrastar / fechar) */}
      {iaEnabled && !chatOpen && <SofiaBubble onOpen={() => setChatOpen(true)} />}

      {showDiscard && (
        <div className="fixed inset-0 bg-ink-950/85 z-[80] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                <ArrowLeft size={20} className="text-amber-700" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-ink-900">Descartar alterações?</h3>
                <p className="text-xs text-ink-500">Você tem dados não salvos nesta tela.</p>
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => { pendingNavRef.current = null; setShowDiscard(false) }}
                className="flex-1 py-2.5 rounded-xl bg-ink-100 hover:bg-ink-200 text-ink-700 font-semibold transition-colors">
                Continuar editando
              </button>
              <button onClick={() => { const a = pendingNavRef.current; pendingNavRef.current = null; setShowDiscard(false); a?.() }}
                className="flex-1 py-2.5 rounded-xl bg-rose-600 hover:bg-rose-700 text-white font-semibold transition-colors">
                Sair sem salvar
              </button>
            </div>
            <p className="mt-4 text-center text-xs text-ink-400">
              <kbd className="px-1.5 py-0.5 rounded bg-ink-100 text-ink-600 font-mono">Esc</kbd> continuar editando
              <span className="mx-2">·</span>
              <kbd className="px-1.5 py-0.5 rounded bg-ink-100 text-ink-600 font-mono">Enter</kbd> sair
            </p>
          </div>
        </div>
      )}

      {showLogout && (
        <div className="fixed inset-0 bg-ink-950/85 z-[80] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-rose-100 flex items-center justify-center">
                <LogOut size={20} className="text-rose-700" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-ink-900">Sair do sistema?</h3>
                <p className="text-xs text-ink-500">Você precisará entrar de novo.</p>
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setShowLogout(false)}
                className="flex-1 py-2.5 rounded-xl bg-ink-100 hover:bg-ink-200 text-ink-700 font-semibold transition-colors">
                Continuar no sistema
              </button>
              <button onClick={() => { logout(); nav('/login') }}
                className="flex-1 py-2.5 rounded-xl bg-rose-600 hover:bg-rose-700 text-white font-semibold transition-colors">
                Sair
              </button>
            </div>
            <p className="mt-4 text-center text-xs text-ink-400">
              <kbd className="px-1.5 py-0.5 rounded bg-ink-100 text-ink-600 font-mono">Esc</kbd> continuar
              <span className="mx-2">·</span>
              <kbd className="px-1.5 py-0.5 rounded bg-ink-100 text-ink-600 font-mono">Enter</kbd> sair
            </p>
          </div>
        </div>
      )}

      <ChangePasswordModal open={changePwOpen} onClose={() => setChangePwOpen(false)} />
    </div>
  )
}
